import {
  TESTIMONIAL_STATUS_TARGETS,
  type TestimonialStatusRow,
  type TestimonialStatusTarget,
} from "../../../../api/pipelineBatchUpdates.service.js";
import { UUID_PATTERN } from "./csvHelpers.js";
import { parseCsvRecords, type CsvRecord } from "./parseCsvRecords.js";

export const TESTIMONIAL_ID_COLUMN = "testimonial_id";
export const STATUS_COLUMN = "status";

export type InvalidTestimonialStatusRow = {
  line: number;
  testimonialId: string;
  status: string;
  reason: string;
};

export type ParsedTestimonialStatusCsv = {
  rows: TestimonialStatusRow[];
  invalid: InvalidTestimonialStatusRow[];
  duplicates: string[];
};

const REQUIRED_COLUMNS = [TESTIMONIAL_ID_COLUMN, STATUS_COLUMN] as const;

function isTargetStatus(value: string): value is TestimonialStatusTarget {
  return (TESTIMONIAL_STATUS_TARGETS as readonly string[]).includes(value);
}

function classifyRows(records: CsvRecord[]): ParsedTestimonialStatusCsv {
  const candidates: TestimonialStatusRow[] = [];
  const invalid: InvalidTestimonialStatusRow[] = [];

  for (const { line, raw, parseError } of records) {
    const testimonialId = (raw[TESTIMONIAL_ID_COLUMN] ?? "").trim().toLowerCase();
    const status = (raw[STATUS_COLUMN] ?? "").trim().toUpperCase();

    if (parseError) {
      invalid.push({ line, testimonialId, status, reason: parseError });
      continue;
    }
    if (!UUID_PATTERN.test(testimonialId)) {
      invalid.push({ line, testimonialId, status, reason: "testimonial_id is not a UUID" });
      continue;
    }
    if (!isTargetStatus(status)) {
      invalid.push({
        line,
        testimonialId,
        status,
        reason: `status must be one of ${TESTIMONIAL_STATUS_TARGETS.join(", ")}`,
      });
      continue;
    }
    candidates.push({ testimonialId, status });
  }

  const occurrences = new Map<string, number>();
  const count = (id: string) => occurrences.set(id, (occurrences.get(id) ?? 0) + 1);
  for (const row of candidates) count(row.testimonialId);
  for (const entry of invalid) {
    if (UUID_PATTERN.test(entry.testimonialId)) count(entry.testimonialId);
  }

  const duplicates = [...occurrences.entries()]
    .filter(([, total]) => total > 1)
    .map(([testimonialId]) => testimonialId);
  const duplicateSet = new Set(duplicates);

  return {
    rows: candidates.filter(row => !duplicateSet.has(row.testimonialId)),
    invalid,
    duplicates,
  };
}

export async function parseTestimonialStatusCsv(
  input: File | string,
): Promise<ParsedTestimonialStatusCsv> {
  return classifyRows(await parseCsvRecords(input, REQUIRED_COLUMNS));
}
